/**
 * Uygulama binding'leri
 * DOM event wiring sorumluluğunu app.ts dışına taşır.
 */

import { doc, updateDoc, setDoc, db, COLLECTIONS, MONTHS } from '../firebase-config.js';
import { AppState } from '../modules/state.js';
import { DocumentsService } from '../features/documents/documents.service.js';
import type { DocumentCategory } from '../features/documents/documents.types.js';

interface AppBindingsDeps {
    initializePublicData: () => void | Promise<void>;
    checkAuth: () => void;
    adminEmail: string;
    totalApartments: number;
    showToast: (message: string, type?: string, options?: any) => any;
    toggleTheme: () => void;
    showSection: (sectionId: string) => void;
    setMenuState: (menuId: string, toggleId: string, isOpen: boolean) => void;
    renderTasks: () => void;
    openModal: (modalId: string) => void;
    closeModal: (modalId: string) => void;
    closeAllModals: () => void;
    renderBills: () => void;
    renderTransactions: () => void;
    updateFinanceSummary: () => void;
    exportTransactionsToExcel: () => void;
    renderDuesTable: () => void;
    updateMonthlyDueAmount: () => Promise<void> | void;
    setDueStatusForMonth: (isPaid: boolean) => Promise<void> | void;
    renderApartments: () => void;
    saveApartment: (data: Record<string, any>) => Promise<void>;
    fileToBase64: (file: File) => Promise<any>;
    updateBill: (id: string, data: Record<string, any>) => any;
    addBill: (data: Record<string, any>) => any;
    updateDecision: (id: string, data: Record<string, any>) => any;
    addDecision: (data: Record<string, any>) => any;
    renderDecisions: () => void;
    renderRecentDecisions: () => void;
    renderResidentDecisions: () => void;
    renderResidentRecentDecisions: () => void;
    updateTransaction: (id: string, data: Record<string, any>) => Promise<boolean> | boolean;
    addTransaction: (data: Record<string, any>) => Promise<boolean> | boolean;
    updateMaintenance: (id: string, data: Record<string, any>) => any;
    addMaintenance: (data: Record<string, any>) => any;
    renderMaintenance: () => void;
    updateTask: (id: string, data: Record<string, any>) => any;
    addTask: (data: Record<string, any>) => any;
    logout: () => Promise<void> | void;
    loginAdmin: (password: string, adminEmail?: string) => Promise<boolean>;
    loginResident: (apartmentNumber: number, password: string) => Promise<boolean>;
    migrateData: () => any;
    updateNotificationPreview: () => void;
    handleNotificationRecipientChange: () => void;
    submitNotificationForm: () => Promise<void>;
    renderDocuments: () => void;
    renderResidentDocuments: () => void;
}

function readResidentPasswordInputs(): { residentPassword: string; residentPasswordConfirm: string } {
    return {
        residentPassword: (document.getElementById('apartment-password') as HTMLInputElement | null)?.value || '',
        residentPasswordConfirm: (document.getElementById('apartment-password-confirm') as HTMLInputElement | null)?.value || ''
    };
}

function bindLoginTabs(): void {
    document.querySelectorAll('.tab-btn').forEach(button => {
        button.addEventListener('click', () => {
            document.querySelectorAll('.tab-btn').forEach(item => item.classList.remove('active'));
            document.querySelectorAll('.login-form').forEach(form => form.classList.remove('active'));
            button.classList.add('active');
            document.getElementById(`${(button as HTMLElement).dataset.tab}-login-form`)?.classList.add('active');
        });
    });
}

function bindAuth(deps: AppBindingsDeps): void {
    document.getElementById('admin-login-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
        if (!submitButton) return;

        const originalText = submitButton.innerHTML;
        try {
            submitButton.innerHTML = '<span class="spinner-sm"></span> Giriş Yapılıyor...';
            submitButton.disabled = true;

            const password = (document.getElementById('admin-password') as HTMLInputElement).value;
            const adminEmail = (document.getElementById('admin-email') as HTMLInputElement | null)?.value?.trim();
            const success = await deps.loginAdmin(password, adminEmail);
            if (!success) {
                deps.showToast('Şifre hatalı veya giriş başarısız. Lütfen tekrar deneyin.', 'error');
            }
        } catch (error) {
            console.error('Login error:', error);
            deps.showToast('Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.', 'error');
        } finally {
            submitButton.innerHTML = originalText;
            submitButton.disabled = false;
        }
    });

    document.getElementById('resident-login-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
        const apartmentNumber = parseInt((document.getElementById('apartment-number') as HTMLInputElement).value, 10);
        const password = (document.getElementById('resident-password') as HTMLInputElement | null)?.value || '';

        if (!Number.isInteger(apartmentNumber) || apartmentNumber < 1 || apartmentNumber > deps.totalApartments) {
            deps.showToast('Lutfen gecerli bir daire secin.', 'error');
            return;
        }

        if (password.trim().length < 6) {
            deps.showToast('Lutfen gecerli bir sakin sifresi girin.', 'error');
            return;
        }

        if (!submitButton) {
            await deps.loginResident(apartmentNumber, password);
            return;
        }

        const originalText = submitButton.innerHTML;

        try {
            submitButton.innerHTML = '<span class="spinner-sm"></span> Giriş Yapılıyor...';
            submitButton.disabled = true;
            const success = await deps.loginResident(apartmentNumber, password);
            if (!success) {
                (document.getElementById('resident-password') as HTMLInputElement | null)?.focus();
            }
        } finally {
            submitButton.innerHTML = originalText;
            submitButton.disabled = false;
        }
    });

    document.getElementById('admin-logout')?.addEventListener('click', event => {
        event.preventDefault();
        deps.logout();
    });
    document.getElementById('admin-mobile-logout')?.addEventListener('click', event => {
        event.preventDefault();
        deps.logout();
    });
    document.getElementById('resident-logout')?.addEventListener('click', event => {
        event.preventDefault();
        deps.logout();
    });
    document.getElementById('resident-mobile-logout')?.addEventListener('click', event => {
        event.preventDefault();
        deps.logout();
    });
    document.getElementById('profile-logout-btn')?.addEventListener('click', () => deps.logout());
}

function bindNavigation(deps: AppBindingsDeps): void {
    document.querySelectorAll('#admin-dashboard .nav-link[data-section]').forEach(link => {
        link.addEventListener('click', event => {
            event.preventDefault();
            deps.showSection((link as HTMLElement).dataset.section || '');
            deps.setMenuState('nav-links', 'mobile-menu-toggle', false);
        });
    });

    document.querySelectorAll('#resident-dashboard .nav-link[data-section]').forEach(link => {
        link.addEventListener('click', event => {
            event.preventDefault();
            deps.showSection((link as HTMLElement).dataset.section || '');
            deps.setMenuState('resident-nav-links', 'resident-mobile-menu-toggle', false);
        });
    });

    document.getElementById('overview-alerts')?.addEventListener('click', event => {
        const card = (event.target as HTMLElement).closest('.overview-alert-card') as HTMLElement | null;
        const target = card?.dataset.target;
        if (target) deps.showSection(target);
    });

    const adminMenuToggle = document.getElementById('mobile-menu-toggle');
    if (adminMenuToggle) {
        adminMenuToggle.setAttribute('aria-controls', 'nav-links');
        adminMenuToggle.setAttribute('aria-expanded', 'false');
        adminMenuToggle.addEventListener('click', () => {
            const navLinks = document.getElementById('nav-links');
            deps.setMenuState('nav-links', 'mobile-menu-toggle', !navLinks?.classList.contains('active'));
        });
    }

    const residentMenuToggle = document.getElementById('resident-mobile-menu-toggle');
    if (residentMenuToggle) {
        residentMenuToggle.setAttribute('aria-controls', 'resident-nav-links');
        residentMenuToggle.setAttribute('aria-expanded', 'false');
        residentMenuToggle.addEventListener('click', () => {
            const navLinks = document.getElementById('resident-nav-links');
            deps.setMenuState('resident-nav-links', 'resident-mobile-menu-toggle', !navLinks?.classList.contains('active'));
        });
    }

    document.querySelectorAll('#resident-tab-bar .tab-item[data-section]').forEach(tab => {
        tab.addEventListener('click', () => {
            document.querySelectorAll('#resident-tab-bar .tab-item').forEach(item => item.classList.remove('active'));
            tab.classList.add('active');
            deps.showSection((tab as HTMLElement).dataset.section || '');
        });
    });

    document.getElementById('tab-menu-toggle')?.addEventListener('click', () => {
        deps.setMenuState('resident-nav-links', 'resident-mobile-menu-toggle', true);
    });

    document.getElementById('menu-close-btn')?.addEventListener('click', () => {
        deps.setMenuState('resident-nav-links', 'resident-mobile-menu-toggle', false);
    });

    document.addEventListener('click', event => {
        const adminNavLinks = document.getElementById('nav-links');
        const adminMenuBtn = document.getElementById('mobile-menu-toggle');
        if (adminNavLinks?.classList.contains('active')) {
            if (!adminNavLinks.contains(event.target as Node) && event.target !== adminMenuBtn && !adminMenuBtn?.contains(event.target as Node)) {
                deps.setMenuState('nav-links', 'mobile-menu-toggle', false);
            }
        }

        const residentNavLinks = document.getElementById('resident-nav-links');
        const residentMenuBtn = document.getElementById('resident-mobile-menu-toggle');
        const tabMenuBtn = document.getElementById('tab-menu-toggle');
        if (residentNavLinks?.classList.contains('active')) {
            if (!residentNavLinks.contains(event.target as Node) && event.target !== residentMenuBtn && event.target !== tabMenuBtn && !residentMenuBtn?.contains(event.target as Node) && !tabMenuBtn?.contains(event.target as Node)) {
                deps.setMenuState('resident-nav-links', 'resident-mobile-menu-toggle', false);
            }
        }
    });
}

function bindFeatureActions(deps: AppBindingsDeps): void {
    document.querySelectorAll('.theme-toggle').forEach(button => {
        button.addEventListener('click', deps.toggleTheme);
    });

    document.querySelectorAll('.filter-btn[data-filter]').forEach(button => {
        button.addEventListener('click', () => {
            document.querySelectorAll('.filter-btn[data-filter]').forEach(item => item.classList.remove('active'));
            button.classList.add('active');
            AppState.currentTaskFilter = (button as HTMLElement).dataset.filter;
            deps.renderTasks();
        });
    });

    document.getElementById('add-bill-btn')?.addEventListener('click', () => {
        document.getElementById('bill-modal-title')!.textContent = 'Fatura Ekle';
        (document.getElementById('bill-id') as HTMLInputElement).value = '';
        deps.openModal('bill-modal');
    });
    document.getElementById('add-decision-btn')?.addEventListener('click', () => {
        document.getElementById('decision-modal-title')!.textContent = 'Karar Ekle';
        (document.getElementById('decision-id') as HTMLInputElement).value = '';
        (document.getElementById('decision-date') as HTMLInputElement).value = new Date().toISOString().split('T')[0];
        deps.openModal('decision-modal');
    });
    document.getElementById('add-transaction-btn')?.addEventListener('click', () => {
        document.getElementById('transaction-modal-title')!.textContent = 'Gelir/Gider Ekle';
        (document.getElementById('transaction-id') as HTMLInputElement).value = '';
        (document.getElementById('transaction-date') as HTMLInputElement).value = new Date().toISOString().split('T')[0];
        deps.openModal('transaction-modal');
    });
    document.getElementById('export-transactions-excel-btn')?.addEventListener('click', deps.exportTransactionsToExcel);
    document.getElementById('add-maintenance-btn')?.addEventListener('click', () => {
        document.getElementById('maintenance-modal-title')!.textContent = 'Bakım Ekle';
        (document.getElementById('maintenance-id') as HTMLInputElement).value = '';
        (document.getElementById('maintenance-date') as HTMLInputElement).value = new Date().toISOString().split('T')[0];
        deps.openModal('maintenance-modal');
    });
    document.getElementById('add-task-btn')?.addEventListener('click', () => {
        document.getElementById('task-modal-title')!.textContent = 'İş Ekle';
        (document.getElementById('task-id') as HTMLInputElement).value = '';
        deps.openModal('task-modal');
    });

    document.getElementById('migrate-data-btn')?.addEventListener('click', deps.migrateData);
}

function bindFilters(deps: AppBindingsDeps): void {
    document.getElementById('bill-year-filter')?.addEventListener('change', deps.renderBills);
    document.getElementById('dues-year-select')?.addEventListener('change', deps.renderDuesTable);
    document.getElementById('monthly-due-amount')?.addEventListener('change', () => deps.updateMonthlyDueAmount());

    const duesBulkMonth = document.getElementById('dues-bulk-month') as HTMLSelectElement | null;
    const duesUnpaidOnly = document.getElementById('dues-unpaid-only') as HTMLInputElement | null;
    const duesMarkPaidBtn = document.getElementById('dues-mark-month-paid');
    const duesMarkUnpaidBtn = document.getElementById('dues-mark-month-unpaid');

    if (duesBulkMonth) {
        duesBulkMonth.value = String(new Date().getMonth() + 1);
        duesBulkMonth.addEventListener('change', deps.renderDuesTable);
    }
    duesUnpaidOnly?.addEventListener('change', deps.renderDuesTable);
    duesMarkPaidBtn?.addEventListener('click', () => deps.setDueStatusForMonth(true));
    duesMarkUnpaidBtn?.addEventListener('click', () => deps.setDueStatusForMonth(false));

    document.getElementById('transaction-type-filter')?.addEventListener('change', () => {
        deps.renderTransactions();
        deps.updateFinanceSummary();
    });
    document.getElementById('transaction-year-filter')?.addEventListener('change', () => {
        deps.renderTransactions();
        deps.updateFinanceSummary();
    });

    document.getElementById('apartment-search')?.addEventListener('input', deps.renderApartments);
    document.getElementById('apartment-status-filter')?.addEventListener('change', deps.renderApartments);
}

function bindFeatureForms(deps: AppBindingsDeps): void {
    document.getElementById('apartment-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const { residentPassword, residentPasswordConfirm } = readResidentPasswordInputs();
        const selectedStatus = (document.getElementById('apartment-status') as HTMLSelectElement).value;

        if (selectedStatus !== 'empty' && residentPassword.trim() !== '') {
            if (residentPassword.trim().length < 6) {
                deps.showToast('Sakin şifresi en az 6 karakter olmalıdır.', 'error');
                return;
            }

            if (residentPassword !== residentPasswordConfirm) {
                deps.showToast('Sakin şifre alanları eşleşmiyor.', 'error');
                return;
            }
        }

        const data = {
            residentName: (document.getElementById('apartment-resident') as HTMLInputElement).value,
            status: selectedStatus,
            ownerName: (document.getElementById('apartment-owner') as HTMLInputElement).value,
            phone: (document.getElementById('apartment-phone') as HTMLInputElement).value,
            residentCount: parseInt((document.getElementById('apartment-count') as HTMLInputElement).value, 10),
            moveInDate: (document.getElementById('apartment-move-in') as HTMLInputElement).value,
            email: (document.getElementById('apartment-email') as HTMLInputElement).value,
            residentPassword,
            residentPasswordConfirm
        };
        try {
            await deps.saveApartment(data);
            deps.closeModal('apartment-modal');
        } catch (error) {
            console.error('Apartment save error:', error);
            deps.showToast(error instanceof Error ? error.message : 'Daire kaydedilirken bir hata oluştu.', 'error');
        }
    });

    document.getElementById('bill-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const id = (document.getElementById('bill-id') as HTMLInputElement).value;
        const data: Record<string, any> = {
            type: (document.getElementById('bill-type') as HTMLSelectElement).value,
            month: parseInt((document.getElementById('bill-month') as HTMLSelectElement).value, 10),
            year: parseInt((document.getElementById('bill-year') as HTMLSelectElement).value, 10),
            amount: parseFloat((document.getElementById('bill-amount') as HTMLInputElement).value),
            notes: (document.getElementById('bill-notes') as HTMLTextAreaElement).value
        };
        const fileInput = document.getElementById('bill-file') as HTMLInputElement;
        if (fileInput.files?.length) {
            const file = fileInput.files[0];
            data.fileData = await deps.fileToBase64(file);
            data.fileType = file.type.includes('pdf') ? 'pdf' : 'image';
        }
        if (id) deps.updateBill(id, data);
        else deps.addBill(data);
        deps.closeModal('bill-modal');
        deps.renderBills();
    });

    document.getElementById('decision-form')?.addEventListener('submit', event => {
        event.preventDefault();
        const id = (document.getElementById('decision-id') as HTMLInputElement).value;
        const data = {
            title: (document.getElementById('decision-title') as HTMLInputElement).value,
            date: (document.getElementById('decision-date') as HTMLInputElement).value,
            content: (document.getElementById('decision-content') as HTMLTextAreaElement).value
        };
        if (id) deps.updateDecision(id, data);
        else deps.addDecision(data);
        deps.closeModal('decision-modal');
        deps.renderDecisions();
        deps.renderRecentDecisions();
        deps.renderResidentDecisions();
        deps.renderResidentRecentDecisions();
    });

    document.getElementById('transaction-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const id = (document.getElementById('transaction-id') as HTMLInputElement).value;
        const data = {
            type: (document.getElementById('transaction-type') as HTMLSelectElement).value,
            category: (document.getElementById('transaction-category') as HTMLSelectElement).value,
            amount: parseFloat((document.getElementById('transaction-amount') as HTMLInputElement).value),
            date: (document.getElementById('transaction-date') as HTMLInputElement).value,
            description: (document.getElementById('transaction-description') as HTMLTextAreaElement).value
        };
        const success = id ? await deps.updateTransaction(id, data) : await deps.addTransaction(data);
        if (!success) return;
        deps.closeModal('transaction-modal');
        deps.renderTransactions();
        deps.updateFinanceSummary();
    });

    document.getElementById('maintenance-form')?.addEventListener('submit', event => {
        event.preventDefault();
        const id = (document.getElementById('maintenance-id') as HTMLInputElement).value;
        const data = {
            title: (document.getElementById('maintenance-title') as HTMLInputElement).value,
            date: (document.getElementById('maintenance-date') as HTMLInputElement).value,
            description: (document.getElementById('maintenance-description') as HTMLTextAreaElement).value,
            status: (document.getElementById('maintenance-status') as HTMLSelectElement).value
        };
        if (id) deps.updateMaintenance(id, data);
        else deps.addMaintenance(data);
        deps.closeModal('maintenance-modal');
        deps.renderMaintenance();
    });

    document.getElementById('task-form')?.addEventListener('submit', event => {
        event.preventDefault();
        const id = (document.getElementById('task-id') as HTMLInputElement).value;
        const data = {
            title: (document.getElementById('task-title') as HTMLInputElement).value,
            description: (document.getElementById('task-description') as HTMLTextAreaElement).value,
            detail: (document.getElementById('task-detail') as HTMLTextAreaElement).value,
            status: (document.getElementById('task-status') as HTMLSelectElement).value,
            priority: (document.getElementById('task-priority') as HTMLSelectElement).value
        };
        if (id) deps.updateTask(id, data);
        else deps.addTask(data);
        deps.closeModal('task-modal');
        deps.renderTasks();
    });
}

function bindResidentProfile(deps: AppBindingsDeps): void {
    document.getElementById('resident-profile-form')?.addEventListener('submit', async event => {
        event.preventDefault();
        const apartmentNo = AppState.currentUser?.apartment;
        const apartment = (AppState.apartments || []).find(item => item.number === apartmentNo);

        if (!apartment) {
            deps.showToast('Daire bulunamadı!', 'error');
            return;
        }

        const selectedStatusRaw = (document.getElementById('profile-status') as HTMLSelectElement)?.value;
        const selectedStatus = ['owner', 'tenant', 'empty'].includes(selectedStatusRaw) ? selectedStatusRaw : 'empty';
        const apartmentDocId = (apartment.id && String(apartment.id).trim()) || `apt_${apartmentNo}`;

        const profilePatch = {
            residentName: (document.getElementById('profile-resident-name') as HTMLInputElement).value.trim(),
            phone: (document.getElementById('profile-phone') as HTMLInputElement).value.trim(),
            email: (document.getElementById('profile-email') as HTMLInputElement).value.trim(),
            residentCount: parseInt((document.getElementById('profile-resident-count') as HTMLInputElement).value, 10) || 0,
            status: selectedStatus,
            updatedAt: new Date().toISOString()
        };

        try {
            await updateDoc(doc(db, COLLECTIONS.APARTMENTS, apartmentDocId), profilePatch);
            const index = (AppState.apartments || []).findIndex(item => item.number === apartmentNo);
            if (index >= 0) {
                AppState.apartments[index] = { ...AppState.apartments[index], ...profilePatch };
            }
            deps.showToast('Bilgileriniz güncellendi!', 'success');
            const mobileProfileName = document.getElementById('mobile-profile-name');
            if (mobileProfileName) mobileProfileName.textContent = profilePatch.residentName || 'Sakin';
        } catch (error: any) {
            console.error('Resident profile update error:', error);
            if (error?.code === 'permission-denied' || error?.code === 'firestore/permission-denied') {
                deps.showToast('Kaydetme yetkisi yok. Güvenlik kuralı güncellemesi gerekiyor.', 'error');
            } else {
                deps.showToast('Bilgiler kaydedilemedi. Lütfen tekrar deneyin.', 'error');
            }
        }
    });
}

function bindNotifications(deps: AppBindingsDeps): void {
    document.getElementById('save-greenapi-btn')?.addEventListener('click', async () => {
        const instanceId = (document.getElementById('greenapi-instance') as HTMLInputElement).value.trim();
        const apiToken = (document.getElementById('greenapi-token') as HTMLInputElement).value.trim();

        if (!instanceId || !apiToken) {
            deps.showToast('Instance ID ve API Token gerekli', 'error');
            return;
        }

        AppState.settings.greenApiIdInstance = instanceId;
        AppState.settings.greenApiToken = apiToken;

        try {
            await setDoc(doc(db, 'settings', 'notifications'), {
                greenApiIdInstance: instanceId,
                greenApiToken: apiToken,
                updatedAt: new Date().toISOString()
            });
            deps.showToast('GREEN-API ayarları kaydedildi', 'success');
        } catch (error) {
            console.error('Error saving settings:', error);
            deps.showToast('Ayarlar kaydedilemedi', 'error');
        }
    });

    const testGreenApiBtn = document.getElementById('test-greenapi-btn') as HTMLButtonElement | null;
    if (testGreenApiBtn) {
        testGreenApiBtn.addEventListener('click', async () => {
            const instanceId = (document.getElementById('greenapi-instance') as HTMLInputElement).value.trim();
            const apiToken = (document.getElementById('greenapi-token') as HTMLInputElement).value.trim();

            if (!instanceId || !apiToken) {
                deps.showToast('Önce Instance ID ve API Token girin', 'error');
                return;
            }

            testGreenApiBtn.textContent = '🔄 Test ediliyor...';
            testGreenApiBtn.disabled = true;

            try {
                const response = await fetch(`https://api.green-api.com/waInstance${instanceId}/getStateInstance/${apiToken}`);
                const result = await response.json();

                if (result.stateInstance === 'authorized') {
                    deps.showToast('✅ Bağlantı başarılı! WhatsApp bağlı.', 'success');
                } else if (result.stateInstance === 'notAuthorized') {
                    deps.showToast('⚠️ WhatsApp bağlı değil. QR kod tarayın.', 'warning');
                } else {
                    deps.showToast(`Durum: ${result.stateInstance || 'Bilinmiyor'}`, 'warning');
                }
            } catch (error: any) {
                deps.showToast(`Bağlantı hatası: ${error.message}`, 'error');
            } finally {
                testGreenApiBtn.textContent = '🔗 Bağlantıyı Test Et';
                testGreenApiBtn.disabled = false;
            }
        });
    }

    document.getElementById('send-due-reminder-btn')?.addEventListener('click', () => {
        const templateSelect = document.getElementById('notification-template') as HTMLSelectElement | null;
        const messageArea = document.getElementById('notification-message') as HTMLTextAreaElement | null;
        const recipientRadio = document.querySelector('input[name="recipient-type"][value="unpaid"]') as HTMLInputElement | null;

        if (templateSelect) templateSelect.value = 'due_reminder';
        if (recipientRadio) recipientRadio.checked = true;
        if (messageArea) {
            const currentMonth = MONTHS[new Date().getMonth()];
            messageArea.value = `Sayın {residentName},\n\n${currentMonth} ayı aidatınızın ödenmediğini hatırlatmak isteriz.\n\nAidat Tutarı: ${AppState.settings.monthlyDueAmount || 500}₺\n\nKolaylıklar dileriz.\nApartman Yönetimi 🏢`;
        }

        deps.openModal('notification-modal');
        deps.updateNotificationPreview();
    });

    document.getElementById('send-general-announcement-btn')?.addEventListener('click', () => {
        const templateSelect = document.getElementById('notification-template') as HTMLSelectElement | null;
        const messageArea = document.getElementById('notification-message') as HTMLTextAreaElement | null;
        const recipientRadio = document.querySelector('input[name="recipient-type"][value="all"]') as HTMLInputElement | null;

        if (templateSelect) templateSelect.value = 'general_message';
        if (recipientRadio) recipientRadio.checked = true;
        if (messageArea) {
            messageArea.value = 'Sayın Sakinlerimiz,\n\n[Duyuru içeriğinizi buraya yazın]\n\nApartman Yönetimi 🏢';
        }

        deps.openModal('notification-modal');
        deps.updateNotificationPreview();
    });

    [document.getElementById('send-notification-btn'), document.getElementById('send-custom-notification-btn')].forEach(button => {
        button?.addEventListener('click', () => {
            deps.openModal('notification-modal');
            deps.updateNotificationPreview();
        });
    });

    const templateSelect = document.getElementById('notification-template') as HTMLSelectElement | null;
    if (templateSelect) {
        templateSelect.addEventListener('change', () => {
            const messageArea = document.getElementById('notification-message') as HTMLTextAreaElement | null;
            const templates: Record<string, string> = {
                due_reminder: `Sayın {residentName},\n\n${MONTHS[new Date().getMonth()]} ayı aidatınızın ödenmediğini hatırlatmak isteriz.\n\nAidat Tutarı: ${AppState.settings.monthlyDueAmount || 500}₺\n\nKolaylıklar dileriz.\nApartman Yönetimi 🏢`,
                maintenance_notice: 'Sayın Sakinlerimiz,\n\n[Tarih] tarihinde [bakım türü] bakımı yapılacaktır.\n\nDetaylar: [Detaylar]\n\nAnlayışınız için teşekkür ederiz.\nApartman Yönetimi 🏢',
                decision_announce: 'Sayın Sakinlerimiz,\n\nYeni bir apartman kararı alınmıştır:\n\n📋 [Karar başlığı]\n\nDetaylar için yönetim panelini ziyaret edebilirsiniz.\n\nApartman Yönetimi 🏢',
                general_message: 'Sayın Sakinlerimiz,\n\n[Mesajınızı buraya yazın]\n\nApartman Yönetimi 🏢',
                custom: ''
            };

            if (messageArea && templates[templateSelect.value] !== undefined) {
                messageArea.value = templates[templateSelect.value];
            }
            deps.updateNotificationPreview();
        });
    }

    document.getElementById('notification-message')?.addEventListener('input', deps.updateNotificationPreview);
    document.querySelectorAll('input[name="recipient-type"]').forEach(radio => {
        radio.addEventListener('change', deps.handleNotificationRecipientChange);
    });

    document.addEventListener('submit', async event => {
        const target = event.target as HTMLFormElement;
        if (target?.id === 'notification-form') {
            event.preventDefault();
            await deps.submitNotificationForm();
        }
    });
}

function bindModalControls(deps: AppBindingsDeps): void {
    document.querySelectorAll('.modal-close, .modal-cancel').forEach(button => {
        button.addEventListener('click', deps.closeAllModals);
    });
    document.querySelectorAll('.modal-overlay').forEach(overlay => {
        overlay.addEventListener('click', deps.closeAllModals);
    });
    document.addEventListener('keydown', event => {
        if (event.key === 'Escape') deps.closeAllModals();
    });
}

function bindDocuments(deps: AppBindingsDeps): void {
    document.getElementById('add-document-btn')?.addEventListener('click', () => {
        (document.getElementById('document-form') as HTMLFormElement | null)?.reset();
        const preview = document.getElementById('document-file-preview');
        if (preview) preview.innerHTML = '';
        deps.openModal('document-modal');
    });

    document.getElementById('document-file')?.addEventListener('change', event => {
        const input = event.target as HTMLInputElement;
        const preview = document.getElementById('document-file-preview');
        if (input.files?.[0] && preview) {
            const file = input.files[0];
            preview.innerHTML = `
                <div class="file-info-chip">
                    <span>${file.name}</span>
                    <span class="file-info-meta">(${(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
            `;
        }
    });

    document.addEventListener('submit', async event => {
        const target = event.target as HTMLFormElement;
        if (target?.id !== 'document-form') return;

        event.preventDefault();
        const submitButton = target.querySelector('button[type="submit"]') as HTMLButtonElement | null;

        try {
            if (submitButton) {
                const btnLoading = submitButton.querySelector('.btn-loading') as HTMLElement | null;
                const btnText = submitButton.querySelector('.btn-text') as HTMLElement | null;
                btnLoading?.classList.remove('is-hidden');
                btnText?.classList.add('is-hidden');
                submitButton.disabled = true;
            }

            const title = (document.getElementById('document-title') as HTMLInputElement).value;
            const category = (document.getElementById('document-category') as HTMLSelectElement).value as DocumentCategory;
            const description = (document.getElementById('document-description') as HTMLTextAreaElement).value;
            const fileInput = document.getElementById('document-file') as HTMLInputElement;
            const isPublic = (document.getElementById('document-public') as HTMLInputElement).checked;

            if (!fileInput.files?.length) {
                deps.showToast('Lütfen bir dosya seçin', 'error');
                throw new Error('No file selected');
            }

            await DocumentsService.upload({
                file: fileInput.files[0],
                title,
                category,
                description,
                isPublic,
                allowedApartments: []
            });

            deps.closeModal('document-modal');
            deps.renderDocuments();
            deps.showToast('Döküman başarıyla yüklendi', 'success');
        } catch (error: any) {
            console.error('Document upload error:', error);
            deps.showToast(`Döküman yüklenirken hata oluştu: ${error.message}`, 'error');
        } finally {
            if (submitButton) {
                const btnLoading = submitButton.querySelector('.btn-loading') as HTMLElement | null;
                const btnText = submitButton.querySelector('.btn-text') as HTMLElement | null;
                btnLoading?.classList.add('is-hidden');
                btnText?.classList.remove('is-hidden');
                submitButton.disabled = false;
            }
        }
    });

    document.getElementById('document-category-filter')?.addEventListener('change', deps.renderDocuments);
    document.getElementById('document-search')?.addEventListener('input', deps.renderDocuments);
    document.getElementById('resident-document-category-filter')?.addEventListener('change', deps.renderResidentDocuments);

    document.getElementById('documents-list')?.addEventListener('click', async event => {
        const deleteButton = (event.target as HTMLElement).closest('.delete-doc-btn') as HTMLElement | null;
        const documentId = deleteButton?.dataset.id;
        if (!documentId) return;
        if (!confirm('Bu dökümanı silmek istediğinize emin misiniz?')) return;

        const success = await DocumentsService.delete(documentId);
        if (success) deps.renderDocuments();
    });
}

export function registerAppBindings(deps: AppBindingsDeps): void {
    document.addEventListener('DOMContentLoaded', () => {
        deps.initializePublicData();
        deps.checkAuth();

        const adminEmailInput = document.getElementById('admin-email') as HTMLInputElement | null;
        if (adminEmailInput && !adminEmailInput.value) {
            adminEmailInput.value = deps.adminEmail || '';
        }

        bindLoginTabs();
        bindAuth(deps);
        bindNavigation(deps);
        bindFeatureActions(deps);
        bindFilters(deps);
        bindFeatureForms(deps);
        bindResidentProfile(deps);
        bindNotifications(deps);
        bindModalControls(deps);
        bindDocuments(deps);
    });
}

function initScrollReveal(): void {
    const revealElements = document.querySelectorAll('.finance-stat-card, .dashboard-card, .glass-card, .transactions-container');
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
            if (entry.isIntersecting) {
                setTimeout(() => {
                    entry.target.classList.add('revealed');
                }, index * 80);
                observer.unobserve(entry.target);
            }
        });
    }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(element => {
        element.classList.add('reveal-on-scroll');
        observer.observe(element);
    });
}

function initParallax(): void {
    const particles = document.querySelector('.particles-container');
    if (!particles) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
        if (!ticking) {
            requestAnimationFrame(() => {
                const scrollY = window.scrollY;
                (particles as HTMLElement).style.transform = `translateY(${scrollY * 0.3}px)`;
                ticking = false;
            });
            ticking = true;
        }
    });
}

export function registerVisualEffectBindings(): void {
    document.addEventListener('DOMContentLoaded', () => {
        initScrollReveal();
        initParallax();
    });
}
